import { getImageSrc } from "../../utils";
import {
  safeString,
  isObject,
  readString,
  buildDecorationOption,
  type MockupDecorationOption,
} from "./mockupUtils";
import { firstNonEmptyString } from "./utils";

/** External ID type used for PromoStandards decoration and location identifiers */
type ExtId = string | number | null | undefined;

export interface ItemDecorationLike {
  ext_decoration_id?: ExtId;
  ext_location_id?: ExtId;
  item_location_id?: string | null;
  item_decoration_id?: string | null;
  imprint_id?: string | null;
  imprint_name?: string | null;
}

export interface ItemArtworkLike {
  artwork_id?: string | null;
  imprint_id?: string | null;
  item_location_id?: string | null;
  image?: Record<string, unknown> | null;
  file?: Record<string, unknown> | null;
  artwork_file_id?: string | null;
}

export interface ItemLocationLike {
  item_location_id?: string | null;
  item_location_title?: string | null;
  item_decorations?: ItemDecorationLike[] | null;
  // some item payloads use this key for per-location decorations
  decorations?: ItemDecorationLike[] | null;
  artworks?: ItemArtworkLike[] | null;
  ext_location_id?: ExtId;
}

export interface ItemLike {
  copied_from?: string | null;
  item_decorations?: ItemDecorationLike[] | null;
  item_locations?: ItemLocationLike[] | null;
  artworks?: ItemArtworkLike[] | null;
}

export interface ProductDecoration {
  artworkImage?: string | null;
  artworkFileId?: string | null;
  decoration: {
    decoration_name: string;
    ps_decoration_id: string;
    item_location_id: string;
    // extra metadata (used for persistence / matching)
    item_decoration_id?: string | null;
    imprint_id?: string | null;
    imprint_name?: string | null;
    ext_decoration_id?: string | number | null;
    ext_location_id?: string | number | null;
  };
}

export interface ProductLocation {
  location_name: string;
  ps_location_id: string;
  ext_location_id?: string | number | null;
  item_location_id?: string | null;
  decorations: ProductDecoration[];
  // Location-level artwork (used when no decorations exist)
  artworkImage?: string | null;
  artworkFileId?: string | null;
  // Imprint ID from artwork (used for unstructured items without decorations)
  artworkImprintId?: string | null;
}

export interface ArtworkIndexValue {
  artworkImage: string | null;
  artworkFileId: string | null;
}

export type ArtworkIndex = Map<string, ArtworkIndexValue>;

export interface PsConfigDecoration {
  ps_decoration_id?: string | number | null;
  decoration_id?: string | number | null;
  decoration_name?: string | null;
  decoration?: string | null;
  name?: string | null;
}

export interface PsConfigLocation {
  ps_location_id?: string | number | null;
  location_name?: string | null;
  decorations?: PsConfigDecoration[] | null;
}

export interface PsConfigLocationOption {
  value: string;
  label: string;
  ps_location_id: string;
  location_name: string;
  decorations: PsConfigDecoration[];
}

const EXCLUDED_DECORATION_NAMES: ReadonlySet<string> = new Set([
  "other (see vendor notes)",
  "font type",
  "graphic design/art redraw",
  "logo dome",
  "logomagic",
  "no imprint (blank goods)",
]);

export function isExcludedDecorationName(name: string): boolean {
  const normalized = name.trim().toLowerCase();
  if (EXCLUDED_DECORATION_NAMES.has(normalized)) return true;
}

export function isPromoStandardItem(item: ItemLike | null | undefined): boolean {
  if (!item) return false;
  return typeof item.copied_from === "string" && item.copied_from.startsWith("ps-");
}

/**
 * Extracts artwork image URL and file ID from an artwork object.
 * Handles both `image` and `file` properties with fallback to getImageSrc.
 */
function extractArtworkFromObject(artwork: ItemArtworkLike): ArtworkIndexValue {
  const imageObj =
    (artwork.image && isObject(artwork.image)) ? artwork.image
    : (artwork.file && isObject(artwork.file)) ? artwork.file
    : null;

  if (!imageObj) {
    return { artworkImage: null, artworkFileId: artwork.artwork_file_id ?? null };
  }

  const artworkImage =
    imageObj?.file_name_large as string | undefined
    || getImageSrc(imageObj, "large")
    || null;

  const artworkFileId =
    (typeof artwork.artwork_file_id === "string" && artwork.artwork_file_id.length > 0)
      ? artwork.artwork_file_id
      : (typeof imageObj?.file_id === "string"
        ? (imageObj.file_id as string)
        : null);

  return { artworkImage, artworkFileId };
}

/**
 * Builds an index of artworks keyed by "locationId:imprintId".
 * This allows fast lookup of artwork associated with a specific decoration.
 *
 * @param artworks - Array of artwork objects to index
 * @returns Map with composite keys and artwork information
 */
export function buildArtworkIndex(artworks: ItemArtworkLike[]): ArtworkIndex {
  const idx: ArtworkIndex = new Map();
  artworks.forEach((artwork) => {
    if (!isObject(artwork)) return;
    const imprintId = typeof artwork.imprint_id === "string" ? artwork.imprint_id : null;
    const locationId = typeof artwork.item_location_id === "string" ? artwork.item_location_id : null;
    if (!imprintId || !locationId) return;

    const extracted = extractArtworkFromObject(artwork);
    if (!extracted.artworkImage) return;

    const key = `${locationId}:${imprintId}`;
    if (!idx.has(key)) idx.set(key, extracted);
  });
  return idx;
}

function decorationOptionId(decoration: ItemDecorationLike): string | null {
  const itemDecorationId = typeof decoration.item_decoration_id === "string" ? decoration.item_decoration_id : null;
  if (itemDecorationId) return itemDecorationId;
  const imprintId = typeof decoration.imprint_id === "string" ? decoration.imprint_id : null;
  if (imprintId) return imprintId;
  const locationId = typeof decoration.item_location_id === "string" ? decoration.item_location_id : null;
  const extDecorationId = decoration.ext_decoration_id ?? null;
  if (!locationId) return null;
  return `${locationId}:${String(extDecorationId ?? "")}`;
}

function canonicalItemLocations(item: ItemLike): ItemLocationLike[] {
  const itemLocations = Array.isArray(item.item_locations) ? item.item_locations : [];
  return itemLocations.filter((l): l is ItemLocationLike => isObject(l));
}

export function getCanonicalDecorations(item: ItemLike): ItemDecorationLike[] {
  const itemLevel = Array.isArray(item.item_decorations) ? item.item_decorations : [];
  const perLocation = canonicalItemLocations(item).flatMap((l) => {
    const decorations = Array.isArray(l.item_decorations) ? l.item_decorations : Array.isArray(l.decorations) ? l.decorations : [];
    return decorations.filter((d): d is ItemDecorationLike => isObject(d));
  });
  return [...itemLevel, ...perLocation].filter((d): d is ItemDecorationLike => isObject(d));
}

export function getCanonicalArtworks(item: ItemLike): ItemArtworkLike[] {
  const itemLevel = Array.isArray(item.artworks) ? item.artworks : [];
  const perLocation = canonicalItemLocations(item).flatMap((l) => {
    const artworks = Array.isArray(l.artworks) ? l.artworks : [];
    return artworks.filter((a): a is ItemArtworkLike => isObject(a));
  });
  return [...itemLevel, ...perLocation].filter((a): a is ItemArtworkLike => isObject(a));
}

export function hasNoPresetConfiguration(item: ItemLike | null | undefined): boolean {
  if (!item) return true;
  const locations = canonicalItemLocations(item);
  if (locations.length === 0) return true;
  const hasAnyDecoration = getCanonicalDecorations(item).length > 0;
  return !hasAnyDecoration;
}

export function parsePsConfigLocations(psConfig: unknown): PsConfigLocationOption[] {
  if (!isObject(psConfig)) return [];
  const rawLocations = (psConfig as Record<string, unknown>).locations;
  if (!Array.isArray(rawLocations)) return [];

  return rawLocations
    .filter((l): l is PsConfigLocation => isObject(l))
    .map((l) => {
      const ps_location_id = safeString(l.ps_location_id);
      const location_name = safeString(l.location_name);
      const decorations = Array.isArray(l.decorations) ? l.decorations.filter((d): d is PsConfigDecoration => isObject(d)) : [];
      return {
        value: ps_location_id,
        label: location_name || ps_location_id,
        ps_location_id,
        location_name,
        decorations,
      };
    })
    .filter((l) => l.ps_location_id.length > 0);
}

export function existingDecorationsToOptions(
  existing: ProductDecoration[],
  locationName: string
): MockupDecorationOption[] {
  return (existing ?? []).map((d) => {
    const dec = d.decoration;
    const id = safeString(dec.item_decoration_id || dec.imprint_id || dec.ps_decoration_id || "");
    return buildDecorationOption({
      id,
      name: safeString(dec.decoration_name),
      locationId: safeString(dec.item_location_id),
      locationName: safeString(locationName),
      itemDecorationId: dec.item_decoration_id ?? null,
      imprintId: dec.imprint_id ?? null,
      imprintName: dec.imprint_name ?? null,
      extDecorationId: dec.ext_decoration_id ?? null,
      extLocationId: dec.ext_location_id ?? null,
      source: "existing",
      artwork: {
        image: d.artworkImage ?? null,
        fileId: d.artworkFileId ?? null,
      },
    });
  });
}

export function psConfigLocationToDecorationOptions(
  location: PsConfigLocationOption
): MockupDecorationOption[] {
  const locName = safeString(location.location_name);

  return (location.decorations ?? []).map((d) => {
    const psDecorationId = safeString(d.ps_decoration_id ?? d.decoration_id ?? "");
    // Some PS payloads send `decoration_name: ""` (empty string). Treat empty strings as missing.
    const name = firstNonEmptyString(d.decoration_name, d.decoration, d.name, psDecorationId);
    return buildDecorationOption({
      id: psDecorationId,
      name,
      locationId: undefined,
      locationName: locName,
      itemDecorationId: null,
      imprintId: null,
      imprintName: name,
      extDecorationId: d.ps_decoration_id ?? d.decoration_id ?? null,
      extLocationId: location.ps_location_id,
      source: "ps-config",
    });
  }).filter((o) => o.value.length > 0);
}

export function getMockupGeneratorItemLocations(
  _state: unknown,
  item: ItemLike | null | undefined
): ProductLocation[] {
  if (!item) return [];

  const locations = canonicalItemLocations(item);
  const allDecorations = getCanonicalDecorations(item);
  const allArtworks = getCanonicalArtworks(item);
  const artworkIndex = buildArtworkIndex(allArtworks);

  return locations.map((loc) => {
    const locationId = typeof loc.item_location_id === "string" ? loc.item_location_id : "";
    const locationName = safeString(loc.item_location_title ?? "");
    const psLocationId = safeString(loc.ext_location_id ?? (locationId || locationName));

    const decorationsAtLocation = allDecorations.filter((d) => d.item_location_id === locationId);
    const decorations: ProductDecoration[] = decorationsAtLocation.map((d) => {
      const imprintId = typeof d.imprint_id === "string" ? d.imprint_id : "";
      const key = imprintId ? `${locationId}:${imprintId}` : "";
      const artwork = key ? artworkIndex.get(key) : undefined;

      const decorationName =
        readString(d.imprint_name) ??
        readString(d.imprint_id) ??
        safeString(d.ext_decoration_id) ??
        "Decoration";

      return {
        artworkImage: artwork?.artworkImage ?? null,
        artworkFileId: artwork?.artworkFileId ?? null,
        decoration: {
          decoration_name: decorationName,
          ps_decoration_id: safeString(d.ext_decoration_id ?? d.imprint_id ?? d.item_decoration_id ?? decorationOptionId(d) ?? ""),
          item_location_id: locationId,
          item_decoration_id: d.item_decoration_id ?? null,
          imprint_id: d.imprint_id ?? null,
          imprint_name: d.imprint_name ?? null,
          ext_decoration_id: d.ext_decoration_id ?? null,
          ext_location_id: d.ext_location_id ?? loc.ext_location_id ?? null,
        },
      };
    });

    const locationArtwork = allArtworks.find((a) => a.item_location_id === locationId);
    const locationArtworkData = locationArtwork ? extractArtworkFromObject(locationArtwork) : null;
    // Extract imprint_id from artwork for unstructured items
    const artworkImprintId = typeof locationArtwork?.imprint_id === "string" ? locationArtwork.imprint_id : null;

    return {
      location_name: locationName || `Location`,
      ps_location_id: psLocationId,
      ext_location_id: loc.ext_location_id ?? null,
      item_location_id: locationId || null,
      decorations,
      artworkImage: locationArtworkData?.artworkImage ?? null,
      artworkFileId: locationArtworkData?.artworkFileId ?? null,
      artworkImprintId,
    };
  });
}
